import { z } from 'zod';
import type { ShippingRateType, DeliveryType } from './shipping.types';

// Form validation schema for shipping rates
export const shippingRateSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  description: z.string().max(500).optional(),
  rateType: z.enum(['flat', 'weight_based', 'price_based', 'free_shipping']),
  deliveryType: z.enum(['standard', 'express', 'same_day', 'pickup']),
  currency: z.enum(['comoros_currency', 'european_currency', 'american_currency']),
  price: z.number().min(0, 'Price must be 0 or more'),
  availableCountries: z.array(z.string()).min(1, 'At least one country is required'),
  freeShippingEnabled: z.boolean(),
  freeShippingThreshold: z.number().min(0).optional(),
  deliverySettingsEnabled: z.boolean(),
  estimatedDeliveryDays: z.number().min(1).optional(),
  maxDeliveryDays: z.number().min(1).optional(),
  weightUnit: z.enum(['kilograms', 'grams']).optional(),
  weightRanges: z.array(z.object({
    minWeight: z.number().min(0),
    maxWeight: z.number().min(0),
    rate: z.number().min(0),
  })).optional(),
  priceRanges: z.array(z.object({
    minPrice: z.number().min(0),
    maxPrice: z.number().min(0),
    rate: z.number().min(0),
  })).optional(),
  isActive: z.boolean().optional(),
});

export type ShippingRateFormData = z.infer<typeof shippingRateSchema>;

// Constants for shipping rate forms
export const SHIPPING_CURRENCIES = [
  { value: 'comoros_currency', label: 'KMF (Franc Comorien)' },
  { value: 'european_currency', label: 'EUR (Euro)' },
  { value: 'american_currency', label: 'USD (Dollar)' },
] as const;

export const SHIPPING_WEIGHT_UNITS = [
  { value: 'kilograms', label: 'kg' },
  { value: 'grams', label: 'g' },
] as const;

export interface RateTypeOption {
  value: ShippingRateType;
  labelKey: string;
  description: string;
}

export interface DeliveryTypeOption {
  value: DeliveryType;
  labelKey: string;
}

// Rate type options with icons
export const SHIPPING_RATE_TYPES: RateTypeOption[] = [
  { value: 'flat', labelKey: 'shipping.types.flat', description: 'shipping.types.flatDesc' },
  { value: 'weight_based', labelKey: 'shipping.types.weight_based', description: 'shipping.types.weightDesc' },
  { value: 'price_based', labelKey: 'shipping.types.price_based', description: 'shipping.types.priceDesc' },
  { value: 'free_shipping', labelKey: 'shipping.types.free_shipping', description: 'shipping.types.freeDesc' },
];

// Delivery type options
export const SHIPPING_DELIVERY_TYPES: DeliveryTypeOption[] = [
  { value: 'standard', labelKey: 'shipping.delivery.standard' },
  { value: 'express', labelKey: 'shipping.delivery.express' },
  { value: 'same_day', labelKey: 'shipping.delivery.same_day' },
  { value: 'pickup', labelKey: 'shipping.delivery.pickup' },
];

// Map server currency format to form currency format
export function mapServerCurrencyToForm(currency: string): 'comoros_currency' | 'european_currency' | 'american_currency' {
  switch (currency?.toLowerCase()) {
    case 'kmf':
    case 'comoros_currency':
      return 'comoros_currency';
    case 'usd':
    case 'american_currency':
      return 'american_currency';
    case 'eur':
    case 'european_currency':
    default:
      return 'european_currency';
  }
}

// Default form values for shipping rate
export const DEFAULT_SHIPPING_RATE_VALUES: ShippingRateFormData = {
  name: '',
  description: '',
  rateType: 'flat',
  deliveryType: 'standard',
  currency: 'european_currency',
  price: 0,
  availableCountries: [],
  freeShippingEnabled: false,
  freeShippingThreshold: undefined,
  deliverySettingsEnabled: false,
  estimatedDeliveryDays: 1,
  maxDeliveryDays: 3,
  weightUnit: 'kilograms',
  weightRanges: [{ minWeight: 0, maxWeight: 0, rate: 0 }],
  priceRanges: [{ minPrice: 0, maxPrice: 0, rate: 0 }],
};
